import { getRequestHeaders } from "./headers";
import { handleError, apiFetch, API_BASE } from './api';

export interface Attachment {
    fileName: string;
    fileKey: string;
    contentType: string;
    size: number;
    uploadedAt?: string;
}

export interface AnnouncementEntry {
    pk: string;
    gsi1pk?: string;
    gsi1sk?: string;
    announcementId: string;
    title: string;
    body: string; // HTML string (kept for legacy records, previews, interop)
    bodyJson?: Record<string, any>; // Tiptap ProseMirror JSON; preferred for rendering when present
    priority: 'normal' | 'high' | 'urgent';
    targetGroups: string[];
    acknowledgementRequired?: boolean;
    attachments: Attachment[];
    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;
}

export interface AcknowledgementSummary {
    alias: string;
    acknowledgedAt: string;
}

export interface UserAcknowledgement {
    acknowledged: boolean;
    acknowledgedAt?: string;
}

export interface AnnouncementsResponse {
    items: AnnouncementEntry[];
    nextToken?: string;
    acknowledgementsMap?: Record<string, AcknowledgementSummary[]>;
    userAcknowledged?: Record<string, UserAcknowledgement>;
}

export async function fetchAnnouncements(): Promise<AnnouncementsResponse> {
    const res = await apiFetch(`${API_BASE}/announcements`, { headers: getRequestHeaders() });
    if (!res.ok) await handleError(res);
    return await res.json();
}

/**
 * Metadata for a file that has already been uploaded directly to S3 via a
 * presigned URL. Only this small metadata travels in the create/update request
 * body — the raw file bytes never traverse the edge-signed API request.
 */
export interface FileMetadata {
    fileName: string;
    contentType: string;
    fileKey: string;
    size?: number;
}

export async function createAnnouncement(input: { title: string; body: string; bodyJson?: Record<string, any>; priority?: string; targetGroups?: string[]; acknowledgementRequired?: boolean; files?: FileMetadata[] }): Promise<AnnouncementEntry> {
    const res = await apiFetch(`${API_BASE}/announcements`, {
        method: 'POST',
        headers: getRequestHeaders('application/json'),
        body: JSON.stringify(input),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

/**
 * Request a presigned S3 PUT URL from the backend so the browser can upload the
 * file bytes directly to S3. The request body here is tiny ({ fileName,
 * contentType }) so it stays below the Lambda@Edge body limit. The backend
 * derives the fileKey server-side and returns it alongside the presigned URL.
 */
export async function getUploadUrl(announcementId: string, fileName: string, contentType: string): Promise<{ uploadUrl: string; fileKey: string }> {
    const res = await apiFetch(`${API_BASE}/announcements/${announcementId}/upload`, {
        method: 'POST',
        headers: getRequestHeaders('application/json'),
        body: JSON.stringify({ fileName, contentType }),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export async function getDownloadUrl(announcementId: string, fileKey: string): Promise<{ downloadUrl: string }> {
    const res = await apiFetch(`${API_BASE}/announcements/${announcementId}/download?fileKey=${encodeURIComponent(fileKey)}`, { headers: getRequestHeaders() });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export interface UpdateAnnouncementInput {
    title?: string;
    body?: string;
    bodyJson?: Record<string, any>;
    priority?: 'normal' | 'high' | 'urgent';
    targetGroups?: string[];
    acknowledgementRequired?: boolean;
    keepAttachments?: string[];
    files?: FileMetadata[];
}

export async function updateAnnouncement(id: string, input: UpdateAnnouncementInput): Promise<AnnouncementEntry> {
    const res = await apiFetch(`${API_BASE}/announcements/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: getRequestHeaders('application/json'),
        body: JSON.stringify(input),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export async function deleteAnnouncement(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/announcements/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getRequestHeaders(),
    });
    if (!res.ok) await handleError(res);
}

export async function acknowledgeAnnouncement(id: string): Promise<{ alias: string; acknowledgedAt: string }> {
    const res = await apiFetch(`${API_BASE}/announcements/${encodeURIComponent(id)}/acknowledge`, {
        method: 'POST',
        headers: getRequestHeaders('application/json'),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export async function fetchAcknowledgements(id: string): Promise<{ acknowledgements: AcknowledgementSummary[] }> {
    const res = await apiFetch(`${API_BASE}/announcements/${encodeURIComponent(id)}/acknowledgements`, {
        headers: getRequestHeaders(),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}
